/**
 * SP5 Quick PDF Export — pure-function helpers.
 *
 * `slugify()` normalises free-form entity names into URL-safe, filename-safe
 * kebab-case tokens. `buildFilename()` composes the final `IntelHub-*.pdf`
 * filename from an `EntityRef` + today's date. `triggerPrint()` sets
 * document.title + a data-export-timestamp attr before calling
 * `window.print()`, then restores both on `afterprint`.
 */

/**
 * Normalise a free-form name into a filename-safe slug.
 *
 * - lowercases everything
 * - strips diacritics via NFKD decomposition + combining-mark removal
 * - collapses runs of non-alphanumerics into single hyphens
 * - trims leading/trailing hyphens
 * - caps at 80 chars and re-trims a trailing hyphen after the cut
 */
export function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80)
    .replace(/-$/, '');
}

export type EntityRef =
  | { type: 'Story'; name: string; slug?: string }
  | { type: 'Vulnerability'; name: string; slug?: string }
  | { type: 'ThreatActor'; name: string; slug?: string }
  | { type: 'RegulatoryFramework'; name: string; slug?: string }
  | { type: 'Analytics'; name: string; extra?: string };

/**
 * Compose a `IntelHub-<Type>-<slug>[-<extra>]-<YYYY-MM-DD>.pdf` filename from
 * an EntityRef + date. Prefers an explicit `slug` (so CVE-style case-
 * sensitive identifiers survive) over `slugify(name)`.
 */
export function buildFilename(entity: EntityRef, today: Date = new Date()): string {
  const slug = 'slug' in entity && entity.slug ? entity.slug : slugify(entity.name);
  const date = today.toISOString().slice(0, 10);
  const extra = 'extra' in entity && entity.extra ? `-${entity.extra}` : '';
  return `IntelHub-${entity.type}-${slug}${extra}-${date}.pdf`;
}

/**
 * Swap document.title to the target filename (sans .pdf), stamp a
 * data-export-timestamp on <body>, then call `window.print()`. On the
 * browser-fired `afterprint` event, both mutations are reverted and the
 * listener is removed. Safe to call repeatedly — each invocation registers
 * its own one-shot restore handler.
 */
export function triggerPrint(entity: EntityRef, today: Date = new Date()): void {
  const previousTitle = document.title;
  const filename = buildFilename(entity, today).replace(/\.pdf$/, '');
  document.title = filename;
  document.body.dataset.exportTimestamp =
    today.toISOString().replace('T', ' ').slice(0, 16) + ' UTC';

  const restore = () => {
    document.title = previousTitle;
    delete document.body.dataset.exportTimestamp;
    window.removeEventListener('afterprint', restore);
  };
  window.addEventListener('afterprint', restore);
  window.print();
}
